// Razorpay integration utilities for web

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  image?: string;
  order_id: string;
  prefill?: {
    email?: string;
    contact?: string;
  };
  theme?: {
    color?: string;
  };
}

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface RazorpayError {
  code: number;
  description: string;
  source: string;
  step: string;
  reason: string;
  metadata: {
    order_id: string;
    payment_id: string;
  };
}

export interface RazorpayCheckoutResponse {
  success?: boolean;
  data?: RazorpayResponse;
  dismissed?: boolean;
  error?: RazorpayError;
}

export function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    // Check if already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export function openRazorpayCheckout(options: RazorpayOptions): Promise<RazorpayCheckoutResponse> {
  return new Promise((resolve) => {
    if (!window.Razorpay) {
      resolve({ error: { code: 0, description: 'Razorpay SDK not loaded', source: 'script', step: 'load', reason: 'script_not_loaded', metadata: { order_id: '', payment_id: '' } } });
      return;
    }

    const rzpOptions = {
      ...options,
      handler: (response: RazorpayResponse) => {
        resolve({ success: true, data: response });
      },
      modal: {
        ondismiss: () => {
          resolve({ dismissed: true });
        }
      }
    };

    const rzp = new window.Razorpay(rzpOptions);
    
    rzp.on('payment.failed', (response: { error: RazorpayError }) => {
      resolve({ error: response.error });
    });
    
    rzp.open();
  });
}

// Global Razorpay interface
declare global {
  interface Window {
    Razorpay: new (options: any) => {
      open(): void;
      on(event: string, callback: (response: any) => void): void;
    };
  }
}